import { Component } from './live-codec.js';

const attachmentTypes = new Map([
    ['start_at_origin', 0], ['follow_origin', 1], ['start_at_customorigin', 2],
    ['start_at_attachment', 3], ['follow_attachment', 4], ['follow_rootbone', 6],
]);

/** mstudioevent_t is 80 bytes; resolve named client events before engine registration. */
export function sequenceParticleEvents(r, sequenceOffset) {
    const count = r.i32(sequenceOffset + 24), start = sequenceOffset + r.i32(sequenceOffset + 28);
    if (count < 0 || count > 1024) throw new Error('Sequence event budget exceeded');
    if (!count) return [];
    r.check(start, count * 80);
    const result = [];
    for (let i = 0; i < count; i++) {
        const at = start + i * 80, type = r.u32(at + 8), nameOffset = r.i32(at + 76);
        const cycle = r.f32(at);
        if (!(type & 1024) && r.i32(at + 4) === 7001) {
            if (cycle >= 0 && cycle <= 1) result.push({ cycle, create: true, name: 'native_footstep', attachType: 0, attachment: 0, footstep: true });
            continue;
        }
        // Disk new-system events are registered by name by Source. Numeric IDs
        // are process-local and cannot identify their meaning before registration.
        if (!(type & 1024) || !nameOffset) continue;
        const name = r.string(at + nameOffset, 128);
        if (!['AE_CL_CREATE_PARTICLE_EFFECT', 'AE_CL_REMOVE_PARTICLE_EFFECT'].includes(name)) continue;
        const options = r.string(at + 12, 64).trim().split(/\s+/);
        if (cycle < 0 || cycle > 1 || !options[0] || /[\x00-\x1f]/.test(options[0])) continue;
        const create = name === 'AE_CL_CREATE_PARTICLE_EFFECT';
        const attachType = create ? attachmentTypes.get(options[1]) : undefined;
        if (create && attachType === undefined) continue;
        const attachment = options[create ? 2 : 1];
        const numeric = attachment === undefined || /^\d+$/.test(attachment);
        const index = attachment === undefined ? -1 : numeric ? Number(attachment) : undefined;
        if (numeric && (!Number.isSafeInteger(index) || index < -1 || index > 65535)) continue;
        result.push({ cycle, create, name: options[0], attachType,
            ...(numeric ? { attachment: index } : { attachmentName: attachment }) });
    }
    return result.toSorted((a, b) => a.cycle - b.cycle);
}

/** Retains effects across sequence changes until an authored remove event. */
export class ModelParticleEvents {
    constructor() { this.tracks = new Map(); this.active = new Map(); this.serial = 0; }
    update(tracks, state, time, rig, modify = name => name) {
        const animation = state.components[Component.Animation];
        const reset = animation?.eventParity ?? ((animation?.flags ?? 0) & 2);
        const seen = new Set();
        for (const [index, track] of (tracks ?? []).entries()) {
            const channel = track.channel ?? `base:${index}`;
            if (track.depth > 0 || !(track.weight > 0) || !track.sequence?.particleEvents?.length) continue;
            seen.add(channel);
            const cycle = Math.max(0, Math.min(1, track.cycle));
            let previous = this.tracks.get(channel);
            const trackReset = track.reset ?? (channel === 'base' ? reset : 0);
            const changed = !previous || previous.sequence !== track.sequence || previous.reset !== trackReset;
            let ranges;
            if (changed) ranges = [[-.01, cycle]];
            else if (cycle >= previous.cycle) ranges = [[previous.cycle, cycle]];
            else if ((track.loop ?? !!(track.sequence.flags & 1)) && previous.cycle - cycle > .5) ranges = [[previous.cycle, 1], [-.01, cycle]];
            else ranges = []; // Source suppresses repeated events on backward corrections.
            const elapsed = previous ? (time - previous.time) / 1000 : 0;
            const distance = previous && !changed ? cycle - previous.cycle + (ranges.length === 2 ? 1 : 0) : 0;
            const observedRate = elapsed > 0 && distance > 0 ? distance / elapsed : 0;
            const nativeRate = Number.isFinite(track.cycleRate) ? Math.abs(track.cycleRate) : 0;
            for (const [rangeIndex, [lo, hi]] of ranges.entries()) for (const event of track.sequence.particleEvents) {
                if (event.cycle <= lo || event.cycle > hi || event.footstep && changed && cycle - event.cycle > .05) continue;
                const name = event.name;
                const index = event.attachmentName
                    ? rig.attachments.findIndex(a => a.name.toLowerCase() === event.attachmentName.toLowerCase()) + 1 : event.attachment;
                if (event.attachmentName && !index || index > 0 && !rig.attachments[index - 1]) continue;
                if (event.create) {
                    if (this.active.size >= 32) this.active.delete(this.active.keys().next().value);
                    const id = ++this.serial;
                    const rate = nativeRate || observedRate;
                    const behind = cycle - event.cycle + (ranges.length === 2 && rangeIndex === 0 ? 1 : 0);
                    // Use observed/native phase speed; scene curves need not
                    // match the first blended clip's nominal duration.
                    this.active.set(id, { ...event, name, attachment: Math.max(0, index), id,
                        startTime: Math.max(0, time - (rate > 0 ? Math.max(0, behind) / rate * 1000 : 0)) });
                } else for (const [id, effect] of this.active)
                    if ((effect.name === name || modify(name) && modify(effect.name) === modify(name))
                        && (index === -1 || index === effect.attachment)) this.active.delete(id);
            }
            // A small backward correction must not move the event watermark back.
            if (!previous || changed || ranges.length) this.tracks.set(channel, { sequence: track.sequence, cycle, reset: trackReset, time });
        }
        for (const channel of this.tracks.keys()) if (!seen.has(channel)) this.tracks.delete(channel);
        // Resolve schema overrides at production time, so a delayed metadata
        // response can reveal an already-crossed event without replaying it.
        return [...this.active.values()].flatMap(effect => {
            if (effect.footstep) {
                if (time - effect.startTime > 1000) { this.active.delete(effect.id); return []; }
                const specs = modify(effect.name, effect);
                return (Array.isArray(specs) ? specs : []).map((spec, i) => ({ ...effect, ...spec, id: `${effect.id}:${i}` }));
            }
            const name = modify(effect.name); return name ? [{ ...effect, name }] : [];
        });
    }
}

export function animatedModelParticleSpecs(entity, modify) {
    if (!entity.rig || !entity.animation || entity.pending || entity.object.visible === false) return [];
    entity.modelParticleEvents ??= new ModelParticleEvents();
    return entity.modelParticleEvents.update(entity.animation.particleTracks, entity.state,
        entity.animation.particleTime ?? entity.state.sampleTime, entity.rig, modify).map(effect => ({
        ...effect, ownerKey: entity.key, source: entity.key, flags: 1,
        offset: effect.offset ?? [0, 0, 0], controlPoints: effect.controlPoints ?? [],
    }));
}
